import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import * as reactTableModule from 'react-table';

const { useTable, usePagination, useSortBy, useGlobalFilter } = reactTableModule.default || reactTableModule;
import { Table, Row, Col } from 'reactstrap';
import { v4 as uuid } from 'uuid';

import { messages } from '../../i18n/messages';

import PaginationToolbar from './PaginationToolbar';
import SearchInput from './SearchInput';
import TableRow from './TableRow';

import styles from './Table.module.scss';

const propTypes = {

	/**
	 * The table title to render, if any.
	 */
	title: PropTypes.node,

	/**
	 * Placeholder text for search input.
	 */
	searchPlaceholder: PropTypes.string,

	/**
	 * The field to use as the key for the table (default is "id").
	 */
	keyField: PropTypes.string,

	/**
	 * The react-table table column parameters to render.
	 *
	 * Refer to react-table documentation for all options:
	 * https://react-table-v7.tanstack.com/docs/api/useTable#column-options
	 *
	 * You can also refer to the specific plugin hook documentation for additional
	 * column options.
	 */
	columns: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The name of the field to access the column id.
		 * Note that this is required if accessor is a function.
		 */
		id: PropTypes.string,

		/**
		 * The label for the column header.
		 */
		Header: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.object,
			PropTypes.func,
		]).isRequired,

		/**
		 * Accessor for the column data.
		 */
		accessor: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,

		/**
		 * Optional function to generate a title for a cell based on the row data.
		 * Function should take raw row data as parameter and return a string to use for
		 * the title attribute on the td element.
		 * (Note that this is our own custom column option).
		 */
		title: PropTypes.func,

	})).isRequired,

	/**
	 * The table data.
	 */
	data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

	/**
	 * Flag indicating whether the initial request for the table
	 * data is being made.
	 */
	initializing: PropTypes.bool,

	/**
	 * Flag indicating whether loading spinner should be shown
	 * for the table.
	 */
	loadingData: PropTypes.bool,

	/**
	 * Flag indicating whether error state should be shown
	 * for the table.
	 */
	hasError: PropTypes.bool,

	/**
	 * The total number of pages to display.
	 * (Required for remote pagination).
	 */
	remotePageCount: PropTypes.number,

	/**
	 * Initial request parameters for the table.
	 */
	initialParams: PropTypes.shape({

		/**
		 * The initial page index.
		 */
		page: PropTypes.number,

		/**
		 * The initial page size.
		 */
		page_size: PropTypes.number,

		/**
		 * The property name to sort by initially.
		 */
		sort_by: PropTypes.string,

		/**
		 * The sort order to sort by initially.
		 */
		sort_order: PropTypes.oneOf(['ASC', 'DESC']),

		/**
		 * The default plain text search query, if any.
		 */
		search: PropTypes.string,

	}),

	/**
	 * Flag indicating whether table should support
	 * sorting columns or not.
	 */
	sort: PropTypes.bool,

	/**
	 * Flag indicating whether table should support
	 * search or not.
	 */
	search: PropTypes.bool,

	/**
	 * Flag indicating whether table should support pagination or not.
	 */
	pagination: PropTypes.bool,

	/**
	 * Flag indicating if rows should have hover styling.
	 */
	hover: PropTypes.bool,

	/**
	 * Optional settings to override default table styling.
	 * See Reactstrap Table documentation for all options.
	 */
	reactstrapTableProps: PropTypes.shape({}),

	/**
	 * Callback to fetch data for table
	 * given current pagination state.
	 */
	fetchData: PropTypes.func,

	/**
	 * Options for rendering additional details beneath a
	 * table row on click.
	 */
	expandRow: PropTypes.shape({

		/**
		 * Render function.
		 */
		render: PropTypes.func.isRequired,

	}),

	/**
	 * List of page size options to display.
	 */
	pageSizeOptions: PropTypes.arrayOf(PropTypes.number),

	/**
	 * Callback to handle behavior when
	 * clicking on a table row.
	 */
	onRowClick: PropTypes.func,

	/**
	 * Optional override of default filter behavior.
	 */
	onSearch: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

	/**
	 * Optional callback for parameter changes.
	 */
	onParamChanges: PropTypes.func,

};

const defaultProps = {

	keyField: 'id',

	sort: false,

	pagination: false,

	search: false,

	initialParams: {},
};

/**
 * Generic table component built using react-table.
 * Data can be fetched remotely or provided directly.
 * See DataTable component for remotely paginated tables.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The Table component.
 */
const TCTable = (props) => {

	const {
		title,
		searchPlaceholder,
		data,
		initializing,
		loadingData,
		hasError,
		columns,
		initialParams,
		remotePageCount,
		keyField,
		sort,
		search,
		pagination,
		hover,
		reactstrapTableProps,
		expandRow,
		pageSizeOptions,
		fetchData,
		onRowClick,
		onSearch,
		onParamChanges,
	} = props;

	const intl = useIntl();

	const remoteFetch = Boolean(fetchData);

	const tableSettings = {
		columns,
		data,
		initialState: {},
		disableSortBy: !sort,
		disableGlobalFilter: !search,
		globalFilter: onSearch,
		// If fetching data remotely, we will need to manually control
		// some of the Table state
		manualSortBy: remoteFetch,
		manualGlobalFilter: remoteFetch,
		manualPagination: remoteFetch,
	};

	// Only set pageCount directly for remotely paginated tables;
	// otherwise results in buggy behavior for front-end pagination
	if (remoteFetch && pagination) {
		tableSettings.pageCount = remotePageCount;
	}

	// Set any initial state for pagination, sort, and search
	if (pagination) {
		tableSettings.initialState.pageIndex = initialParams.page || 0;
		tableSettings.initialState.pageSize = initialParams.page_size || 10;
	}

	if (
		sort &&
		initialParams.sort_by &&
		initialParams.sort_order
	) {
		tableSettings.initialState.sortBy = [{
			id: initialParams.sort_by,
			desc: initialParams.sort_order === 'DESC',
		}];
	}

	if (search && initialParams.search) {
		tableSettings.initialState.globalFilter = initialParams.search;
	}

	// Get table utilities from react-table
	const {
		// Table utils
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		rows,
		// Pagination
		page,
		canNextPage,
		canPreviousPage,
		nextPage,
		previousPage,
		setPageSize,
		pageCount,
		gotoPage,
		// Filter
		setGlobalFilter,
		// Table state
		state: { pageIndex, pageSize, sortBy, globalFilter },
	} = useTable(
		tableSettings,
		useGlobalFilter,
		useSortBy,
		usePagination
	);

	/**
	 * Fetch data remotely for the table if remote options provided.
	 */
	useEffect(() => {

		if (fetchData) {
			fetchData({ pageIndex, pageSize, sortBy, globalFilter });
		}

	}, [
		fetchData,
		pageIndex,
		pageSize,
		sortBy,
		globalFilter
	]);

	/**
	 * Notify parent components of parameter changes.
	 */
	useEffect(() => {

		if (onParamChanges) {

			const sortParams = {};

			if (sortBy && sortBy.length > 0) {
				// NOTE: for now we will only support sorting by one field at a time
				sortParams.sort_by = sortBy[0].id;
				sortParams.sort_order = sortBy[0].desc ? 'DESC' : 'ASC';
			}

			onParamChanges({
				...sortParams,
				page: pageIndex,
				page_size: pageSize,
				search: globalFilter,
			});

		}

	}, [
		onParamChanges,
		pageIndex,
		pageSize,
		sortBy,
		globalFilter
	]);

	const rowsToRender = pagination ? page : rows;

	return (
		<div className="tc-table-container">
			<Row className="mb-2">
				{title &&
					<Col
						className="tc-table-title-container"
						xs="12"
						md={search ? '6' : '6'}
						lg={search ? '8' : '12'}>
						{title}
					</Col>
				}
				{search &&
					<Col
						className="tc-table-search-input-container"
						xs="12"
						md="6"
						lg="4">
						<SearchInput
							searchPlaceholder={searchPlaceholder}
							globalFilter={globalFilter}
							setGlobalFilter={setGlobalFilter} />
					</Col>
				}
			</Row>
			<Row>
				<Col xs="12">
					<Table
						className="tc-datatable"
						hover={hover}
						responsive
						{...reactstrapTableProps}
						{...getTableProps()}>
						<thead>
							{headerGroups.map((headerGroup) => (
								<tr
									key={`header-group-${uuid()}`}
									{...headerGroup.getHeaderGroupProps()}>
									{headerGroup.headers.map((column) => {

										let sortIcon = 'fa-sort';

										if (column.isSorted && column.isSortedDesc) {
											sortIcon = 'fa-sort-down';
										} else if (column.isSorted) {
											sortIcon = 'fa-sort-up';
										}

										return (
											<th
												key={`column-header-${column.id}`}
												{...column.getHeaderProps()}>
												<span>{column.render('Header')}</span>
												{column.canSort &&
													<button
														className="btn"
														{...column.getSortByToggleProps()}>
														<span className={`${styles['sort-icon']} ${column.isSorted ? styles.sorted : ''}`}>
															<i
																className={`fas ${sortIcon}`}
																aria-hidden="true" />
														</span>
													</button>
												}
											</th>
										);
									})}
								</tr>
							))}
						</thead>
						<tbody {...getTableBodyProps()}>
							{loadingData &&
								<tr>
									<td
										colSpan={columns.length}
										className="text-center">
										<i
											className="fas fa-circle-notch fa-spin fa-lg"
											aria-hidden="true"
											aria-label={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_LOADING_LABEL)} />
									</td>
								</tr>
							}
							{!loadingData && hasError &&
								<tr>
									<td
										colSpan={columns.length}
										className="text-center">
										<FormattedMessage
											{...messages.TC_COMPONENTS_TABLE_ERROR_MESSAGE} />
									</td>
								</tr>
							}
							{!loadingData && !hasError && rowsToRender.length === 0 && !initializing &&
								<tr>
									<td
										colSpan={columns.length}
										className="text-center">
										<FormattedMessage
											{...messages.TC_COMPONENTS_TABLE_NO_DATA_MESSAGE} />
									</td>
								</tr>
							}
							{!loadingData && !hasError && rowsToRender.length > 0 && rowsToRender.map((row) => {

								prepareRow(row);

								return (
									<TableRow
										key={row.original[keyField]}
										row={row}
										keyField={keyField}
										expandRow={expandRow}
										onRowClick={onRowClick} />
								);

							})}
						</tbody>
					</Table>
				</Col>
			</Row>
			{pagination &&
				<PaginationToolbar
					pageSize={pageSize}
					pageIndex={pageIndex}
					pageCount={pageCount}
					hasNext={canNextPage}
					hasPrevious={canPreviousPage}
					pageSizeOptions={pageSizeOptions}
					gotoPage={gotoPage}
					onSelectPageSize={setPageSize}
					onNext={nextPage}
					onPrevious={previousPage} />
			}
		</div>
	);

};

TCTable.propTypes = propTypes;
TCTable.defaultProps = defaultProps;

export default TCTable;
